import React, { useMemo } from "react";
import { People, Send, User } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import UserAvatar from "@/components/controls/UserAvatar";
import HighlightedText from "@/components/controls/HighlightedText";
import { Notes } from "@/entities/activities/types";
import { useUsersAll } from "@/entities/users/hooks";
import { filter, map, take } from "lodash";

interface Props {
  notes: Notes | null;
  searchQuery: string;
}

const EmailParticipants: React.FC<Props> = ({ notes, searchQuery }) => {
  const { users } = useUsersAll();

  const fromEmail = notes?.fromEmail;
  const fromName = fromEmail?.name || fromEmail?.email || "";

  const sender = useMemo(
    () =>
      fromEmail?.email
        ? users.find(
            (user) =>
              user.email?.toLowerCase() === fromEmail.email.toLowerCase()
          )
        : undefined,
    [users, fromEmail?.email]
  );

  const toNames = useMemo(() => {
    const names = filter(
      map(notes?.toEmails, (recipient) => recipient.name || recipient.email),
      Boolean
    );
    const firstTwo = take(names, 2);
    const extraCount = Math.max(0, names.length - 2);
    return `${firstTwo.join(", ")}${extraCount ? ` +${extraCount}` : ""}`;
  }, [notes?.toEmails]);

  return (
    <div className="flex items-center gap-2 py-[6px]">
      {fromName ? (
        <div className="flex items-center gap-[6px]">
          {sender ? (
            <UserAvatar userData={sender} size="S" />
          ) : (
            <IconContainer className="!size-[18px]" color="secondary">
              <User />
            </IconContainer>
          )}
          <Text variant="bodySecondary">
            <HighlightedText
              text={fromName}
              query={searchQuery}
              className="whitespace-pre-wrap"
            />
          </Text>
        </div>
      ) : null}
      {toNames ? (
        <>
          <IconContainer className="!size-[18px]" color="secondary">
            <Send />
          </IconContainer>
          <div className="flex items-center gap-[6px]">
            <IconContainer className="!size-[18px]" color="secondary">
              <People />
            </IconContainer>
            <Text variant="bodySecondary">
              <HighlightedText
                text={toNames}
                query={searchQuery}
                className="whitespace-pre-wrap"
              />
            </Text>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default EmailParticipants;
